import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { convertOdds } from '../utils/oddsConverter'
import '../styles/app.css'

const s = {
  container: { width: '100%', margin: 0, padding: '0', backgroundColor: '#010714', minHeight: '100vh', boxSizing: 'border-box' },
  card: { backgroundColor: 'transparent', borderRadius: '10px', boxShadow: 'none', border: '1px solid #1b2538', padding: '16px', margin: '16px 20px' },
  row: { display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' },
  col: { display: 'flex', flexDirection: 'column', gap: '12px' },
  title: { fontSize: '24px', fontWeight: '600', margin: 0, color: '#f1f5f9', letterSpacing: '0.3px' },
  subtitle: { fontSize: '20px', fontWeight: '600', margin: 0, color: '#f1f5f9' },
  label: { fontWeight: '500', color: '#f1f5f9', fontSize: '14px' },
  text: { fontSize: '14px', color: '#94a3b8' },
  input: { border: '1px solid #1b2538', borderRadius: '8px', padding: '12px 16px', backgroundColor: 'transparent', color: '#f1f5f9', fontSize: '14px', width: '100%' },
  select: { border: '1px solid #1b2538', borderRadius: '8px', padding: '12px 16px', backgroundColor: 'transparent', color: '#f1f5f9', fontSize: '14px', width: '100%' },
  resultCard: { backgroundColor: 'transparent', borderRadius: '12px', padding: '24px', border: '1px solid #1b2538', textAlign: 'center', maxWidth: '300px', margin: '0 auto' },
  resultValue: { fontSize: '36px', fontWeight: '700', margin: '8px 0', lineHeight: '1' },
  resultLabel: { fontSize: '16px', color: '#94a3b8', fontWeight: '500' },
  inputGrid: { display: 'flex', gap: '48px', flexWrap: 'wrap' },
  inputItem: { flex: '0 0 200px' },
  arbResultGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginTop: '20px' },
  arbResultCard: { backgroundColor: 'transparent', borderRadius: '8px', padding: '16px', border: '1px solid #1b2538', textAlign: 'center' },
  arbResultValue: { fontSize: '20px', fontWeight: '600', margin: '4px 0', lineHeight: '1', color: '#f1f5f9' },
  arbResultLabel: { fontSize: '12px', color: '#94a3b8', fontWeight: '500' },
  roundingSelect: { border: '1px solid #1b2538', borderRadius: '8px', padding: '8px 12px', backgroundColor: 'transparent', color: '#f1f5f9', fontSize: '14px', maxWidth: '120px' }
}

export default function BettingCalculators() {
  const { oddsFormat } = useAuth()
  const [selectedCalculator, setSelectedCalculator] = useState('arbitrage')
  
  // Arbitrage Calculator State
  const [arbInputs, setArbInputs] = useState({
    odds1: '',
    odds2: '',
    totalStake: '',
    stake1: '',
    stake2: '',
    profit1: '',
    profit2: '',
    rounding: 'none'
  })
  
  // EV Calculator State
  const [evInputs, setEvInputs] = useState({
    odds: '',
    fairOdds: ''
  })

  // Bonus Bet Turnover State
  const [bonusInputs, setBonusInputs] = useState({
    betfairCommission: '6',
    betfairLayOdds: '',
    bookmakerOdds: '',
    bookmakerStake: ''
  })

  // Back and Lay State
  const [backLayInputs, setBackLayInputs] = useState({
    betfairCommission: '6',
    betfairLayOdds: '',
    bookmakerOdds: '',
    bookmakerStake: ''
  })

  const calculators = [
    { id: 'arbitrage', name: 'Arbitrage Calculator', icon: '⚖️' },
    { id: 'ev', name: 'Expected Value Calculator', icon: '📈' },
    { id: 'bonusTurnover', name: 'Bonus Bet Turnover', icon: '💰' },
    { id: 'backLay', name: 'Back and Lay Calculator', icon: '🔄' }
  ]

  // Apply rounding helper
  const applyRounding = (amount, roundingType) => {
    if (roundingType === '1') return Math.round(amount)
    if (roundingType === '5') return Math.round(amount / 5) * 5
    return amount
  }

  // Update calculations when any field changes
  const updateArbCalculation = (field, value) => {
    const odds1 = parseFloat(field === 'odds1' ? value : arbInputs.odds1)
    const odds2 = parseFloat(field === 'odds2' ? value : arbInputs.odds2)
    const totalStake = parseFloat(field === 'totalStake' ? value : arbInputs.totalStake)
    let stake1 = parseFloat(field === 'stake1' ? value : arbInputs.stake1)
    let stake2 = parseFloat(field === 'stake2' ? value : arbInputs.stake2)
    
    if (!odds1 || !odds2 || odds1 <= 0 || odds2 <= 0) return
    
    // If changing total stake or odds, auto-calculate optimal stakes
    if (field === 'totalStake' || field === 'odds1' || field === 'odds2') {
      if (totalStake && totalStake > 0) {
        const margin = (1/odds1) + (1/odds2)
        let newStake1 = totalStake * (1/odds1) / margin
        let newStake2 = totalStake * (1/odds2) / margin
        
        // Apply rounding
        if (arbInputs.rounding !== 'none') {
          newStake1 = applyRounding(newStake1, arbInputs.rounding)
          newStake2 = applyRounding(newStake2, arbInputs.rounding)
        }
        
        const profit1 = newStake1 * odds1 - (newStake1 + newStake2)
        const profit2 = newStake2 * odds2 - (newStake1 + newStake2)
        
        setArbInputs(prev => ({
          ...prev,
          [field]: value,
          stake1: newStake1.toFixed(2),
          stake2: newStake2.toFixed(2),
          profit1: profit1.toFixed(2),
          profit2: profit2.toFixed(2)
        }))
      } else {
        setArbInputs(prev => ({ ...prev, [field]: value }))
      }
    }
    // If changing stakes, recalculate profits
    else if (field === 'stake1' || field === 'stake2') {
      if (arbInputs.rounding !== 'none') {
        stake1 = field === 'stake1' ? applyRounding(stake1, arbInputs.rounding) : stake1
        stake2 = field === 'stake2' ? applyRounding(stake2, arbInputs.rounding) : stake2
      }
      
      const profit1 = stake1 * odds1 - (stake1 + stake2)
      const profit2 = stake2 * odds2 - (stake1 + stake2)
      
      setArbInputs(prev => ({
        ...prev,
        [field]: field === 'stake1' ? stake1.toString() : stake2.toString(),
        stake1: stake1.toString(),
        stake2: stake2.toString(),
        profit1: profit1.toFixed(2),
        profit2: profit2.toFixed(2),
        totalStake: (stake1 + stake2).toFixed(2)
      }))
    }
    // If changing profits, recalculate stakes
    else if (field === 'profit1' || field === 'profit2') {
      const profit = parseFloat(value)
      if (isNaN(profit)) return
      
      if (field === 'profit1' && stake2) {
        const newStake1 = (profit + stake1 + stake2) / odds1
        const adjustedStake1 = arbInputs.rounding !== 'none' ? applyRounding(newStake1, arbInputs.rounding) : newStake1
        const newProfit1 = adjustedStake1 * odds1 - (adjustedStake1 + stake2)
        const newProfit2 = stake2 * odds2 - (adjustedStake1 + stake2)
        
        setArbInputs(prev => ({
          ...prev,
          stake1: adjustedStake1.toFixed(2),
          profit1: newProfit1.toFixed(2),
          profit2: newProfit2.toFixed(2),
          totalStake: (adjustedStake1 + stake2).toFixed(2)
        }))
      } else if (field === 'profit2' && stake1) {
        const newStake2 = (profit + stake1 + stake2) / odds2
        const adjustedStake2 = arbInputs.rounding !== 'none' ? applyRounding(newStake2, arbInputs.rounding) : newStake2
        const newProfit1 = stake1 * odds1 - (stake1 + adjustedStake2)
        const newProfit2 = adjustedStake2 * odds2 - (stake1 + adjustedStake2)
        
        setArbInputs(prev => ({
          ...prev,
          stake2: adjustedStake2.toFixed(2),
          profit1: newProfit1.toFixed(2),
          profit2: newProfit2.toFixed(2),
          totalStake: (stake1 + adjustedStake2).toFixed(2)
        }))
      }
    }
  }

  // Calculate if arbitrage exists
  const isArbitrage = () => {
    const odds1 = parseFloat(arbInputs.odds1)
    const odds2 = parseFloat(arbInputs.odds2)
    if (!odds1 || !odds2 || odds1 <= 0 || odds2 <= 0) return false
    return (1/odds1) + (1/odds2) < 1
  }


  // EV Calculator Logic
  const calculateEV = () => {
    const odds = parseFloat(evInputs.odds)
    const fairOdds = parseFloat(evInputs.fairOdds)
    
    if (!odds || !fairOdds || odds <= 0 || fairOdds <= 0) {
      return null
    }
    
    const fairProbability = 1 / fairOdds
    const evPercentage = ((fairProbability * odds - 1) * 100)
    
    return {
      evPercentage: evPercentage.toFixed(2),
      isPositiveEV: evPercentage > 0
    }
  }

  // Bonus Bet Turnover Logic
  const calculateBonusTurnover = () => {
    const commission = parseFloat(bonusInputs.betfairCommission)
    const betfairLayOdds = parseFloat(bonusInputs.betfairLayOdds)
    const bookmakerOdds = parseFloat(bonusInputs.bookmakerOdds)
    const bookmakerStake = parseFloat(bonusInputs.bookmakerStake)
    
    if (!commission || !betfairLayOdds || !bookmakerOdds || !bookmakerStake || 
        commission < 0 || betfairLayOdds <= 0 || bookmakerOdds <= 0 || bookmakerStake <= 0) {
      return null
    }
    
    // Calculate betfair stake using correct formula
    const betfairStake = Math.round(((bookmakerOdds - 1) / (betfairLayOdds - commission/100)) * bookmakerStake * 100) / 100
    
    // Calculate betfair liability
    const betfairLiability = betfairStake * (betfairLayOdds - 1)
    
    // Calculate commission
    const commissionAmount = (commission / 100) * betfairStake
    
    // Calculate total outcome
    const totalOutcome = betfairStake - commissionAmount
    
    // Betfair wins = same as total outcome
    const betfairWins = totalOutcome
    
    // Bookmaker wins = (bookmaker odds - 1) * bookmaker stake
    const bookmakerWins = (bookmakerOdds - 1) * bookmakerStake
    
    // Total outcome as percentage
    const outcomePercentage = (totalOutcome / bookmakerStake) * 100
    
    return {
      betfairStake: betfairStake.toFixed(2),
      betfairLiability: betfairLiability.toFixed(2),
      totalOutcome: totalOutcome.toFixed(2),
      outcomePercentage: outcomePercentage.toFixed(2),
      bookmakerWins: bookmakerWins.toFixed(2),
      betfairWins: betfairWins.toFixed(2),
      commission: commissionAmount.toFixed(2)
    }
  }

  // Back and Lay Calculator Logic
  const calculateBackLay = () => {
    const commission = parseFloat(backLayInputs.betfairCommission)
    const betfairLayOdds = parseFloat(backLayInputs.betfairLayOdds)
    const bookmakerOdds = parseFloat(backLayInputs.bookmakerOdds)
    const bookmakerStake = parseFloat(backLayInputs.bookmakerStake)
    
    if (!commission || !betfairLayOdds || !bookmakerOdds || !bookmakerStake || 
        commission < 0 || betfairLayOdds <= 0 || bookmakerOdds <= 0 || bookmakerStake <= 0) {
      return null
    }
    
    // Calculate betfair stake: book odds/(bf lay odds-bf commission)*book stake
    const betfairStake = (bookmakerOdds / (betfairLayOdds - commission/100)) * bookmakerStake
    
    // Calculate betfair liability: betfair stake*(bf lay odds-1)
    const betfairLiability = betfairStake * (betfairLayOdds - 1)
    
    // Calculate commission: bf commission * bf stake
    const commissionAmount = (commission / 100) * betfairStake
    
    // Calculate total outcome: bf stake - commission
    const totalOutcome = betfairStake - commissionAmount
    
    // Betfair wins: bf stake - bf commission (same as total outcome)
    const betfairWins = totalOutcome
    
    // Bookmaker wins: book odds * book stake
    const bookmakerWins = bookmakerOdds * bookmakerStake
    
    // Total outcome as percentage: total outcome / book stake
    const outcomePercentage = (totalOutcome / bookmakerStake) * 100
    
    return {
      betfairStake: betfairStake.toFixed(2),
      betfairLiability: betfairLiability.toFixed(2),
      totalOutcome: totalOutcome.toFixed(2),
      outcomePercentage: outcomePercentage.toFixed(2),
      bookmakerWins: bookmakerWins.toFixed(2),
      betfairWins: betfairWins.toFixed(2),
      commission: commissionAmount.toFixed(2)
    }
  }

  const evResults = calculateEV()
  const bonusResults = calculateBonusTurnover()
  const backLayResults = calculateBackLay()

  return (
    <div className="bc-container tool-unified calc-container" style={s.container}>
      <style>{`
        /* Global body scrollbar styling */
        body::-webkit-scrollbar,
        html::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        body::-webkit-scrollbar-track,
        html::-webkit-scrollbar-track {
          background: #010714;
        }
        body::-webkit-scrollbar-thumb,
        html::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 3px;
          transition: background 0.2s;
        }
        body:hover::-webkit-scrollbar-thumb,
        html:hover::-webkit-scrollbar-thumb {
          background: #475569;
        }
        body::-webkit-scrollbar-thumb:hover,
        html::-webkit-scrollbar-thumb:hover {
          background: #64748b !important;
        }

        /* Custom scrollbar for hover-only visibility */
        .calc-scrollable::-webkit-scrollbar,
        .calc-container::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .calc-scrollable::-webkit-scrollbar-track,
        .calc-container::-webkit-scrollbar-track {
          background: #010714;
        }
        .calc-scrollable::-webkit-scrollbar-thumb,
        .calc-container::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 3px;
          transition: background 0.2s;
        }
        .calc-scrollable:hover::-webkit-scrollbar-thumb,
        .calc-container:hover::-webkit-scrollbar-thumb {
          background: #475569;
        }
        .calc-scrollable::-webkit-scrollbar-thumb:hover,
        .calc-container::-webkit-scrollbar-thumb:hover {
          background: #64748b !important;
        }

        /* Firefox */
        body,
        html {
          scrollbar-width: thin;
          scrollbar-color: transparent #010714;
        }
        body:hover,
        html:hover {
          scrollbar-color: #475569 #010714;
        }
        .calc-scrollable,
        .calc-container {
          scrollbar-width: thin;
          scrollbar-color: transparent #010714;
        }
        .calc-scrollable:hover,
        .calc-container:hover {
          scrollbar-color: #475569 #010714;
        }
      `}</style>
      {/* Header */}
      <div className="bc-header">
        <div className="bc-header-titlewrap">
          <div className="bc-header-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="2" width="16" height="20" rx="2"/>
              <line x1="8" y1="6" x2="16" y2="6"/>
              <line x1="8" y1="10" x2="8" y2="10"/>
              <line x1="12" y1="10" x2="12" y2="10"/>
              <line x1="16" y1="10" x2="16" y2="10"/>
              <line x1="8" y1="14" x2="8" y2="14"/>
              <line x1="12" y1="14" x2="12" y2="14"/>
              <line x1="16" y1="14" x2="16" y2="14"/>
              <line x1="8" y1="18" x2="8" y2="18"/>
              <line x1="12" y1="18" x2="16" y2="18"/>
            </svg>
          </div>
          <div className="bc-header-textcol">
            <div className="bc-header-titlerow">
              <h1 className="bc-title">Calculators</h1>
            </div>
          </div>
        </div>
        <div className="bc-header-actions">
          <select
            value={selectedCalculator}
            onChange={(e) => setSelectedCalculator(e.target.value)}
            style={s.select}
          >
            {calculators.map(calc => (
              <option key={calc.id} value={calc.id}>
                {calc.icon} {calc.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Arbitrage Calculator */}
      {selectedCalculator === 'arbitrage' && (
        <div style={s.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <h2 style={s.subtitle}>⚖️ Arbitrage Calculator</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ ...s.label, fontSize: '12px' }}>Rounding:</label>
              <select
                value={arbInputs.rounding}
                onChange={(e) => {
                  const newRounding = e.target.value
                  setArbInputs({...arbInputs, rounding: newRounding})
                  
                  // Apply rounding to existing stakes
                  if (arbInputs.stake1 && arbInputs.stake2) {
                    const stake1 = parseFloat(arbInputs.stake1)
                    const stake2 = parseFloat(arbInputs.stake2)
                    const odds1 = parseFloat(arbInputs.odds1)
                    const odds2 = parseFloat(arbInputs.odds2)
                    
                    if (stake1 && stake2 && odds1 && odds2) {
                      const roundedStake1 = applyRounding(stake1, newRounding)
                      const roundedStake2 = applyRounding(stake2, newRounding)
                      
                      const profit1 = roundedStake1 * odds1 - (roundedStake1 + roundedStake2)
                      const profit2 = roundedStake2 * odds2 - (roundedStake1 + roundedStake2)
                      
                      setArbInputs(prev => ({
                        ...prev,
                        rounding: newRounding,
                        stake1: roundedStake1.toFixed(2),
                        stake2: roundedStake2.toFixed(2),
                        profit1: profit1.toFixed(2),
                        profit2: profit2.toFixed(2),
                        totalStake: (roundedStake1 + roundedStake2).toFixed(2)
                      }))
                    }
                  }
                }}
                style={s.roundingSelect}
              >
                <option value="none">None</option>
                <option value="1">To $1</option>
                <option value="5">To $5</option>
              </select>
            </div>
          </div>
          
          {/* Odds Row */}
          <div style={s.inputGrid}>
            <div style={s.inputItem}>
              <label style={s.label}>Odds 1</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="number"
                  step="0.01"
                  placeholder="2.10"
                  value={arbInputs.odds1}
                  onChange={(e) => {
                    setArbInputs({...arbInputs, odds1: e.target.value})
                    updateArbCalculation('odds1', e.target.value)
                  }}
                  style={s.input}
                />
                {oddsFormat !== 'decimal' && arbInputs.odds1 && <span style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}>({convertOdds(arbInputs.odds1, oddsFormat)})</span>}
              </div>
            </div>
            <div style={s.inputItem}>
              <label style={s.label}>Odds 2</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="number"
                  step="0.01"
                  placeholder="2.05"
                  value={arbInputs.odds2}
                  onChange={(e) => {
                    setArbInputs({...arbInputs, odds2: e.target.value})
                    updateArbCalculation('odds2', e.target.value)
                  }}
                  style={s.input}
                />
                {oddsFormat !== 'decimal' && arbInputs.odds2 && <span style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}>({convertOdds(arbInputs.odds2, oddsFormat)})</span>}
              </div>
            </div>
          </div>

          {/* Total Stake Row */}
          <div style={{ ...s.inputGrid, marginTop: '16px' }}>
            <div style={s.inputItem}>
              <label style={s.label}>Total Stake</label>
              <input
                type="number"
                step="0.01"
                placeholder="$100"
                value={arbInputs.totalStake}
                onChange={(e) => {
                  setArbInputs({...arbInputs, totalStake: e.target.value})
                  updateArbCalculation('totalStake', e.target.value)
                }}
                style={s.input}
              />
            </div>
          </div>

          {/* Stakes Row */}
          <div style={{ ...s.inputGrid, marginTop: '16px' }}>
            <div style={s.inputItem}>
              <label style={s.label}>Stake 1</label>
              <input
                type="number"
                step="0.01"
                placeholder="$48.78"
                value={arbInputs.stake1}
                onChange={(e) => {
                  setArbInputs({...arbInputs, stake1: e.target.value})
                  updateArbCalculation('stake1', e.target.value)
                }}
                style={s.input}
              />
            </div>
            <div style={s.inputItem}>
              <label style={s.label}>Stake 2</label>
              <input
                type="number"
                step="0.01"
                placeholder="$51.22"
                value={arbInputs.stake2}
                onChange={(e) => {
                  setArbInputs({...arbInputs, stake2: e.target.value})
                  updateArbCalculation('stake2', e.target.value)
                }}
                style={s.input}
              />
            </div>
          </div>

          {/* Profits Row */}
          <div style={{ ...s.inputGrid, marginTop: '16px' }}>
            <div style={s.inputItem}>
              <label style={s.label}>Profit 1</label>
              <input
                type="text"
                placeholder="$2.44"
                value={arbInputs.profit1 ? `$${arbInputs.profit1}` : ''}
                readOnly
                style={{
                  ...s.input,
                  backgroundColor: 'transparent',
                  color: isArbitrage() ? '#34d399' : '#ef4444',
                  fontWeight: '600'
                }}
              />
            </div>
            <div style={s.inputItem}>
              <label style={s.label}>Profit 2</label>
              <input
                type="text"
                placeholder="$2.44"
                value={arbInputs.profit2 ? `$${arbInputs.profit2}` : ''}
                readOnly
                style={{
                  ...s.input,
                  backgroundColor: 'transparent',
                  color: isArbitrage() ? '#34d399' : '#ef4444',
                  fontWeight: '600'
                }}
              />
            </div>
          </div>

        </div>
      )}

      {/* EV Calculator */}
      {selectedCalculator === 'ev' && (
        <div style={s.card}>
          <h2 style={s.subtitle}>📈 Expected Value Calculator</h2>
          
          <div style={{ maxWidth: '300px' }}>
            <div style={s.col}>
              <div>
                <label style={s.label}>Odds</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="2.50"
                    value={evInputs.odds}
                    onChange={(e) => setEvInputs({...evInputs, odds: e.target.value})}
                    style={s.input}
                  />
                  {oddsFormat !== 'decimal' && evInputs.odds && <span style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}>({convertOdds(evInputs.odds, oddsFormat)})</span>}
                </div>
              </div>
              <div>
                <label style={s.label}>Fair Odds</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="2.20"
                    value={evInputs.fairOdds}
                    onChange={(e) => setEvInputs({...evInputs, fairOdds: e.target.value})}
                    style={s.input}
                  />
                  {oddsFormat !== 'decimal' && evInputs.fairOdds && <span style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}>({convertOdds(evInputs.fairOdds, oddsFormat)})</span>}
                </div>
              </div>
              <div>
                <label style={s.label}>EV %</label>
                <input
                  type="text"
                  placeholder={evResults ? `${evResults.isPositiveEV ? '+' : ''}${evResults.evPercentage}%` : '0.00%'}
                  value={evResults ? `${evResults.isPositiveEV ? '+' : ''}${evResults.evPercentage}%` : ''}
                  readOnly
                  style={{
                    ...s.input,
                    backgroundColor: 'transparent',
                    color: evResults && evResults.isPositiveEV ? '#34d399' : '#ef4444',
                    fontWeight: '600'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bonus Bet Turnover Calculator */}
      {selectedCalculator === 'bonusTurnover' && (
        <div style={s.card}>
          <h2 style={s.subtitle}>💰 Bonus Bet Turnover</h2>
          
          {/* Inputs */}
          <div style={{ maxWidth: '400px' }}>
            <div style={s.col}>
              <div>
                <label style={s.label}>Betfair Commission (%)</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="6"
                  value={bonusInputs.betfairCommission}
                  onChange={(e) => setBonusInputs({...bonusInputs, betfairCommission: e.target.value})}
                  style={s.input}
                />
              </div>
              <div>
                <label style={s.label}>Betfair Lay Odds</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="7.4"
                    value={bonusInputs.betfairLayOdds}
                    onChange={(e) => setBonusInputs({...bonusInputs, betfairLayOdds: e.target.value})}
                    style={s.input}
                  />
                  {oddsFormat !== 'decimal' && bonusInputs.betfairLayOdds && <span style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}>({convertOdds(bonusInputs.betfairLayOdds, oddsFormat)})</span>}
                </div>
              </div>
              <div>
                <label style={s.label}>Bookmaker Odds</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="6.25"
                    value={bonusInputs.bookmakerOdds}
                    onChange={(e) => setBonusInputs({...bonusInputs, bookmakerOdds: e.target.value})}
                    style={s.input}
                  />
                  {oddsFormat !== 'decimal' && bonusInputs.bookmakerOdds && <span style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}>({convertOdds(bonusInputs.bookmakerOdds, oddsFormat)})</span>}
                </div>
              </div>
              <div>
                <label style={s.label}>Bookmaker Stake</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="$50"
                  value={bonusInputs.bookmakerStake}
                  onChange={(e) => setBonusInputs({...bonusInputs, bookmakerStake: e.target.value})}
                  style={s.input}
                />
              </div>
            </div>
          </div>

          {/* Results */}
          {bonusResults && (
            <div style={{ marginTop: '24px' }}>
              {/* Calculated Fields */}
              <div style={{ ...s.inputGrid, marginBottom: '16px' }}>
                <div style={s.inputItem}>
                  <label style={s.label}>Betfair Stake</label>
                  <input
                    type="text"
                    value={`$${bonusResults.betfairStake}`}
                    readOnly
                    style={{
                      ...s.input,
                      backgroundColor: 'transparent',
                      color: '#f1f5f9',
                      fontWeight: '600'
                    }}
                  />
                </div>
                <div style={s.inputItem}>
                  <label style={s.label}>Betfair Liability</label>
                  <input
                    type="text"
                    value={`$${bonusResults.betfairLiability}`}
                    readOnly
                    style={{
                      ...s.input,
                      backgroundColor: 'transparent',
                      color: '#f1f5f9',
                      fontWeight: '600'
                    }}
                  />
                </div>
              </div>

              {/* Total Outcome */}
              <div style={{ ...s.inputGrid, marginBottom: '16px' }}>
                <div style={s.inputItem}>
                  <label style={s.label}>Total Outcome</label>
                  <input
                    type="text"
                    value={`$${bonusResults.totalOutcome} (${bonusResults.outcomePercentage}%)`}
                    readOnly
                    style={{
                      ...s.input,
                      backgroundColor: 'transparent',
                      color: '#34d399',
                      fontWeight: '600'
                    }}
                  />
                </div>
              </div>

              {/* Win Scenarios */}
              <div style={{ ...s.inputGrid, marginBottom: '16px' }}>
                <div style={s.inputItem}>
                  <label style={s.label}>Betfair Wins</label>
                  <input
                    type="text"
                    value={`$${bonusResults.betfairWins}`}
                    readOnly
                    style={{
                      ...s.input,
                      backgroundColor: 'transparent',
                      color: '#f1f5f9',
                      fontWeight: '600'
                    }}
                  />
                </div>
                <div style={s.inputItem}>
                  <label style={s.label}>Bookmaker Wins</label>
                  <input
                    type="text"
                    value={`$${bonusResults.bookmakerWins}`}
                    readOnly
                    style={{
                      ...s.input,
                      backgroundColor: 'transparent',
                      color: '#f1f5f9',
                      fontWeight: '600'
                    }}
                  />
                </div>
              </div>

              {/* Commission */}
              <div style={{ ...s.inputGrid, marginBottom: '16px' }}>
                <div style={s.inputItem}>
                  <label style={s.label}>Commission</label>
                  <input
                    type="text"
                    value={`$${bonusResults.commission}`}
                    readOnly
                    style={{
                      ...s.input,
                      backgroundColor: 'transparent',
                      color: '#f59e0b',
                      fontWeight: '600'
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Back and Lay Calculator */}
      {selectedCalculator === 'backLay' && (
        <div style={s.card}>
          <h2 style={s.subtitle}>🔄 Back and Lay Calculator</h2>
          
          {/* Inputs */}
          <div style={{ maxWidth: '400px' }}>
            <div style={s.col}>
              <div>
                <label style={s.label}>Betfair Commission (%)</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="6"
                  value={backLayInputs.betfairCommission}
                  onChange={(e) => setBackLayInputs({...backLayInputs, betfairCommission: e.target.value})}
                  style={s.input}
                />
              </div>
              <div>
                <label style={s.label}>Betfair Lay Odds</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="1.35"
                    value={backLayInputs.betfairLayOdds}
                    onChange={(e) => setBackLayInputs({...backLayInputs, betfairLayOdds: e.target.value})}
                    style={s.input}
                  />
                  {oddsFormat !== 'decimal' && backLayInputs.betfairLayOdds && <span style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}>({convertOdds(backLayInputs.betfairLayOdds, oddsFormat)})</span>}
                </div>
              </div>
              <div>
                <label style={s.label}>Bookmaker Odds</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="1.32"
                    value={backLayInputs.bookmakerOdds}
                    onChange={(e) => setBackLayInputs({...backLayInputs, bookmakerOdds: e.target.value})}
                    style={s.input}
                  />
                  {oddsFormat !== 'decimal' && backLayInputs.bookmakerOdds && <span style={{ fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap' }}>({convertOdds(backLayInputs.bookmakerOdds, oddsFormat)})</span>}
                </div>
              </div>
              <div>
                <label style={s.label}>Bookmaker Stake</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="$50"
                  value={backLayInputs.bookmakerStake}
                  onChange={(e) => setBackLayInputs({...backLayInputs, bookmakerStake: e.target.value})}
                  style={s.input}
                />
              </div>
            </div>
          </div>

          {/* Results */}
          {backLayResults && (
            <div style={{ marginTop: '24px' }}>
              {/* Calculated Fields */}
              <div style={{ ...s.inputGrid, marginBottom: '16px' }}>
                <div style={s.inputItem}>
                  <label style={s.label}>Betfair Stake</label>
                  <input
                    type="text"
                    value={`$${backLayResults.betfairStake}`}
                    readOnly
                    style={{
                      ...s.input,
                      backgroundColor: 'transparent',
                      color: '#f1f5f9',
                      fontWeight: '600'
                    }}
                  />
                </div>
                <div style={s.inputItem}>
                  <label style={s.label}>Betfair Liability</label>
                  <input
                    type="text"
                    value={`$${backLayResults.betfairLiability}`}
                    readOnly
                    style={{
                      ...s.input,
                      backgroundColor: 'transparent',
                      color: '#f1f5f9',
                      fontWeight: '600'
                    }}
                  />
                </div>
              </div>

              {/* Total Outcome and QL/Profit */}
              <div style={{ ...s.inputGrid, marginBottom: '16px' }}>
                <div style={s.inputItem}>
                  <label style={s.label}>Total Outcome</label>
                  <input
                    type="text"
                    value={`$${backLayResults.totalOutcome} (${backLayResults.outcomePercentage}%)`}
                    readOnly
                    style={{
                      ...s.input,
                      backgroundColor: 'transparent',
                      color: '#34d399',
                      fontWeight: '600'
                    }}
                  />
                </div>
                <div style={s.inputItem}>
                  <label style={s.label}>QL / Profit</label>
                  <input
                    type="text"
                    value={`$${(parseFloat(backLayResults.totalOutcome) - parseFloat(backLayInputs.bookmakerStake)).toFixed(2)}`}
                    readOnly
                    style={{
                      ...s.input,
                      backgroundColor: 'transparent',
                      color: parseFloat(backLayResults.totalOutcome) - parseFloat(backLayInputs.bookmakerStake) > 0 ? '#34d399' : '#ef4444',
                      fontWeight: '600'
                    }}
                  />
                </div>
              </div>

              {/* Win Scenarios */}
              <div style={{ ...s.inputGrid, marginBottom: '16px' }}>
                <div style={s.inputItem}>
                  <label style={s.label}>Betfair Wins</label>
                  <input
                    type="text"
                    value={`$${backLayResults.betfairWins}`}
                    readOnly
                    style={{
                      ...s.input,
                      backgroundColor: 'transparent',
                      color: '#f1f5f9',
                      fontWeight: '600'
                    }}
                  />
                </div>
                <div style={s.inputItem}>
                  <label style={s.label}>Bookmaker Wins</label>
                  <input
                    type="text"
                    value={`$${backLayResults.bookmakerWins}`}
                    readOnly
                    style={{
                      ...s.input,
                      backgroundColor: 'transparent',
                      color: '#f1f5f9',
                      fontWeight: '600'
                    }}
                  />
                </div>
              </div>

              {/* Commission */}
              <div style={{ ...s.inputGrid, marginBottom: '16px' }}>
                <div style={s.inputItem}>
                  <label style={s.label}>Commission</label>
                  <input
                    type="text"
                    value={`$${backLayResults.commission}`}
                    readOnly
                    style={{
                      ...s.input,
                      backgroundColor: 'transparent',
                      color: '#f59e0b',
                      fontWeight: '600'
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}